/**
 * Qui a le droit de faire quoi dans le back-office.
 *
 * Le code testait jusqu'ici `role === 'admin' || role === 'moderator'` à une
 * dizaine d'endroits, ce qui revenait à donner à un modérateur les mêmes
 * pouvoirs qu'à un administrateur : suspendre un compte, lire le journal
 * d'audit, créer des codes promo. Ce fichier est désormais la seule source de
 * vérité — la barre latérale, les gardes de route et les boutons d'action s'y
 * réfèrent tous.
 *
 * Il double le contrôle du serveur, il ne le remplace pas : masquer un bouton
 * n'a jamais protégé une route. Les deux listes doivent rester cohérentes
 * (voir requireStaff.js et requireAdmin.js côté API).
 */

export type Role = 'admin' | 'moderator' | 'user';

/** Une section de navigation, ou une action sensible sur une ressource. */
export type Permission =
  | 'section.dashboard'
  | 'section.trips'
  | 'section.fleet'
  | 'section.analytics'
  | 'section.communication'
  | 'section.promo'
  | 'section.support'
  | 'section.users'
  | 'section.verifications'
  | 'section.logs'
  | 'section.fees'
  | 'verification.review'
  | 'verification.override'
  | 'user.suspend'
  | 'user.verify'
  | 'user.delete'
  | 'promo.manage'
  // Trois pouvoirs distincts sur une annonce, et non un seul « gérer la
  // flotte » : corriger une annonce relève de la modération, la supprimer ou
  // la faire changer de mains engage la plateforme. Alignés sur
  // `loadVehicleForWrite` / `resolveOwnerChange` côté API.
  | 'vehicle.edit'
  | 'vehicle.delete'
  | 'vehicle.reassign';

const ADMIN: Permission[] = [
  'section.dashboard',
  'section.trips',
  'section.fleet',
  'section.analytics',
  // Écrire aux utilisateurs (campagnes, push, e-mails) engage la plateforme.
  'section.communication',
  'section.promo',
  'section.support',
  'section.users',
  'section.verifications',
  // Journaux : l'historique des actions de l'équipe et les journaux API
  // (requêtes et réponses de tous les comptes) — administrateurs seulement,
  // comme GET /api/admin/logs et /api/admin-audit-logs (requireAdmin).
  'section.logs',
  // Frais Dingo et règlements des propriétaires : l'argent, administrateurs
  // seulement (requireAdmin sur /api/admin/fees).
  'section.fees',
  'verification.review',
  'verification.override',
  'user.suspend',
  'user.verify',
  'user.delete',
  'promo.manage',
  'vehicle.edit',
  'vehicle.delete',
  'vehicle.reassign'
];

/**
 * Le modérateur instruit des dossiers : il voit la file de vérification,
 * ouvre un dossier, en regarde les images, approuve ou refuse. Il consulte la
 * liste des utilisateurs pour rattacher un dossier à une personne, et répond
 * au support. Rien de ce qui engage la plateforme — l'argent, les codes, le
 * journal, la suspension d'un compte — ne lui est ouvert.
 */
const MODERATOR: Permission[] = [
  'section.dashboard',
  'section.trips',
  'section.fleet',
  'section.support',
  'section.users',
  'section.verifications',
  'verification.review',
  // Corriger une annonce et la suspendre, oui : c'est le métier. La supprimer
  // (définitif, emporte photos et historique) ou la donner à un autre compte,
  // non.
  'vehicle.edit'
];

const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  admin: ADMIN,
  moderator: MODERATOR,
  user: []
};

export function can(role: string | undefined, permission: Permission): boolean {
  if (!role) return false;
  const granted = ROLE_PERMISSIONS[role as Role];
  return Array.isArray(granted) && granted.includes(permission);
}

/** Le back-office n'est pas destiné aux comptes ordinaires. */
export function isStaff(role: string | undefined): boolean {
  return role === 'admin' || role === 'moderator';
}

export function roleLabel(role: string | undefined): string {
  if (role === 'admin') return 'Administrateur';
  if (role === 'moderator') return 'Modérateur';
  return 'Utilisateur';
}

/**
 * Section vers permission, pour les gardes de route et la barre latérale.
 * Une section absente de cette table est considérée comme ouverte à tout le
 * personnel (profil, paramètres).
 */
export const SECTION_PERMISSION: Record<string, Permission> = {
  dashboard: 'section.dashboard',
  mytrips: 'section.trips',
  myrentals: 'section.fleet',
  analytics: 'section.analytics',
  // Écrire à tous les utilisateurs engage la plateforme : administrateurs
  // seulement (requireAdmin côté API sur /api/marketing et les push libres).
  communication: 'section.communication',
  'promo-codes': 'section.promo',
  support: 'section.support',
  users: 'section.users',
  verifications: 'section.verifications',
  journaux: 'section.logs',
  frais: 'section.fees'
};

export function canOpenSection(role: string | undefined, section: string): boolean {
  const permission = SECTION_PERMISSION[section];
  if (!permission) return isStaff(role);
  return can(role, permission);
}
